<template>
    <modal-centered-base
    fullScreenDialog
    noFooterButtons
    withTopCloseButton
    modalWidth="448px"
    titleIcon="link"
    :title="t('modals.shareLink.title')"
    @hideMe="emit('hideMe')">
        <div class="align-info" :class="{'mobile': getMobileAppState}">
            <!-- Aux Text -->
            <infobox-generic :label="t('modals.shareLink.informationBlockText')"/>
            <!-- Expiry Date InputCalendar -->
            <input-calendar
            :params="{ labelText: t('modals.shareLink.expiryDateLabel'), placeholder: t('modals.shareLink.expiryDatePlaceholder') }"
            :disabledDates="{ to: new Date() }"
            :inputValue="expiryDate"
            @input="[expiryDate = $event, disabledGenerateLinkButton = false]"/>
            <!-- Generate Link Button -->
            <button-secondary
            buttonSize="small"
            class="generate-button"
            :label="t('modals.shareLink.generateLink')"
            :disabled="disabledGenerateLinkButton"
            @clicked="generateLink"/>
            <div v-if="linkTextBlockParams.content && disabledGenerateLinkButton" class="after-generation">
                <!-- Share Link Text Block -->
                <text-block-generic :params="linkTextBlockParams"/>
                <!-- Copy Link Button -->
                <button-secondary
                buttonSize="small"
                class="copy-button"
                :label="t('modals.shareLink.copyLink')" @clicked="copyToClipboard"/>
            </div>
        </div>
        <!-- Loader -->
        <loader-generic v-if="showLoader" isOverlay/>
    </modal-centered-base>
    <!-- Toast -->
    <toast-generic
    v-if="showToast"
    :feedbackText="feedbackToastParams.text"
    :toastType="feedbackToastParams.toastType"
    :buttonsParams="buttonsParams"
    @hideMe="showToast = false"/>
</template>

<script setup>
import ModalCenteredBase from '@/components/generic/modals/centered/Base'
import InfoboxGeneric from '@/components/generic/infobox/Generic'
import InputCalendar from '@/components/generic/inputs/Calendar'
import ButtonSecondary from '@/components/generic/buttons/Secondary'
import TextBlockGeneric from '@/components/generic/textBlocks/Generic'
import LoaderGeneric from '@/components/generic/loaders/Generic'
import ToastGeneric from '@/components/generic/toasts/Generic'
/* Vue */
import { ref, onBeforeUnmount } from 'vue'
/* Store */
import { storeToRefs } from 'pinia'
import { useProjectBaseStore } from '@/store/projectBase'
import { useShareAndSendInfoStore } from '@/store/shareAndSendInfo'

/* i18n */
import { useI18n } from 'vue-i18n'
const { t } = useI18n()

/* Emmitters */
const emit = defineEmits(['hideMe'])

/* Props
@operationId {String, Number} - operationId for which we want to create a shareLink for receipt
@payload {Object} with:
    queryParams {Object} - query params to add to request
    dataParams {Object} - data  params to add to request
@subTitle {String}
*/
const props = defineProps({
    operationId: [String, Number],
    payload: Object,
    subTitle: String
})

const showLoader = ref(false)
const disabledGenerateLinkButton = ref(false)
const expiryDate = ref(null)
const linkTextBlockParams = ref({})

const shareAndSendInfoStore = useShareAndSendInfoStore()
const { generateShareLink } = shareAndSendInfoStore
const projectBaseStore = useProjectBaseStore()
const { getMobileAppState } = storeToRefs(projectBaseStore)
const { updatePreventMobileFeatureBack, updatePreventMobileSideModalBack } = projectBaseStore

const generateLink = async () => {
    showLoader.value = true
    const payload = {
        params: {
            operationId: props.operationId,
            expirationDate: expiryDate.value ? expiryDate.value : null
        },
        data: props.payload ? props.payload.dataParams : {}
    }

    if (props.payload && props.payload.queryParams) payload.params = { ...payload.params, ...props.payload.queryParams }

    try {
        const res = await generateShareLink(payload)

        linkTextBlockParams.value = {
            label: t('modals.shareLink.shareLinkLabel'),
            content: res.data.data.link
        }

        disabledGenerateLinkButton.value = true

        showToastHandler(res.data)
    } catch (error) {
        showToastHandler(error.response && error.response.data ? error.response.data : { errorLevel: 'error' })
    }
}

const copyToClipboard = () => {
    if (window.cordova && window.cordova.plugins.clipboard) {
        window.cordova.plugins.clipboard.copy(linkTextBlockParams.value.content)
    } else {
        navigator.clipboard.writeText(linkTextBlockParams.value.content)
    }

    showToastHandler({
        message: t('modals.shareLink.linkCopied'),
        errorLevel: 'info'
    })
}

/*************************************************
*                                                *
*               Feedback Toast Logic             *
*                                                *
*************************************************/
const showToast = ref(false)
const feedbackToastParams = ref({ text: '', toastType: 'info' })

const showToastHandler = response => {
    showLoader.value = false

    feedbackToastParams.value = {
        text: response.message || t('genericErrorMessage'),
        toastType: response.errorLevel.toLowerCase()
    }
    showToast.value = true
}

/***********************************************************
*                                                          *
*                 MOBILE NATIVE BACK LOGIC                 *
*                                                          *
***********************************************************/
/* Preving mobile back to trigger features close */
updatePreventMobileFeatureBack(true)
updatePreventMobileSideModalBack(true)
onBeforeUnmount(() => {
	updatePreventMobileFeatureBack(false)
    updatePreventMobileSideModalBack(false)
})

const triggerClose = ref(false)
const onBackKeyDown = () => {
    triggerClose.value = true
}

document.addEventListener('backbutton', onBackKeyDown, false)
</script>

<style lang="scss" scoped>
.align-info {
    width: 100%;
    height: auto;

    display: flex;
    flex-direction: column;
    align-items: flex-start;
    flex-shrink: 0;

    .infobox-generic-wrapper {
        margin-bottom: 24px;
    }

    .input-elements-container {
        margin-bottom: 8px;
    }

    .after-generation {
        width: 100%;
        height: auto;
        margin-top: 24px;
        padding-top: 24px;

        display: flex;
        flex-direction: column;
        align-items: flex-start;
        flex-shrink: 0;

        border-top: 1px solid var(--secondary-light-2);

        .text-blocks-wrapper {
            margin-bottom: 16px;
        }
    }

    &.mobile {
        .generate-button {
            width: 100%;
        }

        .copy-button {
            width: 100%;
        }
    }
}
</style>
